/**
 * pdfStrings.js — Centralized translations for anything rendered inside a PDF.
 *
 * We deliberately keep this *separate* from the UI i18n dictionary because:
 *  - PDFs are printed and can outlive a session
 *  - The user can choose a per-PDF language distinct from the UI locale
 *  - Arabic strings need to be validated character-by-character (Amiri font)
 *
 * Every consumer imports `pdfStr(lang)` which returns a plain object. All keys
 * are ALWAYS present in every language — never fall back to English inside a PDF.
 */

export const PDF_STRINGS = {
  es: {
    // Common
    tracking: "Seguimiento",
    tracking_code: "Código de seguimiento",
    reference: "Referencia",
    payment_info: "Datos de pago",
    bank: "Banco",
    beneficiary: "Titular",
    date: "Fecha",
    origin: "Origen",
    destination: "Destino",
    weight: "Peso",
    weight_kg: "Peso (kg)",
    cbm: "CBM",
    mode: "Modo",
    container: "Contenedor",
    vehicle: "Vehículo",
    driver: "Conductor",
    departure: "Salida",
    arrival: "Llegada",
    total: "Total",
    subtotal: "Subtotal",
    quantity: "Cantidad",
    unit_price: "Precio unitario",
    currency: "Moneda",
    // Modes
    mode_aereo: "AÉREO",
    mode_terrestre: "TERRESTRE",
    mode_maritimo: "MARÍTIMO",
    // Titles
    label_title: "Etiqueta de envío",
    manifest_title: "Manifiesto de salida",
    manifest_copy: "Copia de manifiesto",
    quote_title: "Cotización",
    quote_ref: "Referencia",
    receipt_title: "Recibo",
    photo_evidence: "Evidencia fotográfica",
    // Sender/receiver
    sender: "Remitente",
    receiver: "Destinatario",
    from: "DESDE",
    to: "PARA",
    address: "Dirección",
    phone: "Teléfono",
    email: "Correo",
    tax_id: "NIF",
    // Package
    package: "Bulto",
    packages: "Bultos",
    piece: "Pieza",
    pieces: "Piezas",
    description: "Descripción",
    contents: "Contenido",
    items: "Items",
    box: "Caja",
    boxes: "Cajas",
    // Manifest
    shipments: "Envíos",
    count: "Cantidad",
    route: "Ruta",
    not_found: "No encontrados",
    // Pricing
    base_price: "Precio base",
    variable: "Variable",
    margin: "Margen",
    waybill_fee: "Cargo por guía",
    merchandise: "Mercancía",
    surcharge: "Recargo",
    valid_until: "Válido hasta",
    days: "días",
    invoice_title: "Factura",
    invoice_number: "Nº Factura",
    due_date: "Vencimiento",
    vat: "IVA",
    balance: "Saldo",
    paid: "Pagado",
    client: "Cliente",
    status: "Estado",
    office: "Sucursal",
    bank_details: "Datos bancarios",
    remarks: "Observaciones",
    statement_title: "Extracto de cuenta",
    statement_of_cliente: "Cliente",
    statement_of_proveedor: "Proveedor",
    statement_of_oficina: "Sucursal",
    statement_of_general: "Resumen",
    period: "Periodo",
    all_dates: "Todo el histórico",
    opening_balance: "Saldo inicial",
    closing_balance: "Saldo final",
    total_charges: "Total cargos",
    total_credits: "Total abonos",
    movement: "Movimiento",
    charge: "Cargo",
    credit: "Abono",
    no_movements: "Sin movimientos en el periodo",
    mov_factura: "Factura",
    mov_cobro: "Cobro",
    mov_gasto: "Gasto",
    mov_factura_compra: "Factura compra",
    mov_pago: "Pago",
    mov_cargo: "Cargo",
    mov_abono: "Abono",
    inv_status_pendiente: "Pendiente",
    inv_status_parcial: "Parcial",
    inv_status_pagada: "Pagada",
    // Footer
    footer_tagline: "Meos Cargo · Plataforma de logística empresarial",
    brand_tagline: "SOLUCIONES QUE MUEVEN TU MUNDO",
    brand_slogan: "Comprometidos con tu crecimiento",
    invoice_subtitle: "Comprobante fiscal",
    folio: "Folio",
    issue_date: "Fecha de emisión",
    payment_method: "Método de pago",
    bank_transfer: "Transferencia bancaria",
    client_data: "Datos del cliente",
    name: "Nombre",
    address: "Dirección",
    amount: "Importe",
    discount: "Descuento",
    observations: "Observaciones",
    thanks_note: "Gracias por su preferencia. Para cualquier duda, favor de contactarnos.",
    authorized_by: "Autorizado por:",
    cfdi_note: "Documento electrónico. Verifica su autenticidad con el código QR.",
    track_at: "Rastrear en",
    signature: "Firma",
    printed_on: "Impreso el",
    page: "Página",
    // Delivery/pickup
    delivery: "Entrega",
    pickup: "Recogida",
    delivered_to: "Entregado a",
    received_by: "Recibido por",
    id_document: "Documento ID",
    notes: "Notas",
    // Vouchers (expense / payment)
    expense_voucher: "COMPROBANTE DE GASTO",
    payment_voucher: "COMPROBANTE DE COBRO",
    category: "Categoría",
    supplier: "Proveedor",
    method: "Método",
  },
  en: {
    tracking: "Tracking",
    tracking_code: "Tracking code",
    reference: "Reference",
    payment_info: "Payment info",
    bank: "Bank",
    beneficiary: "Holder",
    date: "Date",
    origin: "Origin",
    destination: "Destination",
    weight: "Weight",
    weight_kg: "Weight (kg)",
    cbm: "CBM",
    mode: "Mode",
    container: "Container",
    vehicle: "Vehicle",
    driver: "Driver",
    departure: "Departure",
    arrival: "Arrival",
    total: "Total",
    subtotal: "Subtotal",
    quantity: "Quantity",
    unit_price: "Unit price",
    currency: "Currency",
    mode_aereo: "AIR",
    mode_terrestre: "GROUND",
    mode_maritimo: "SEA",
    label_title: "Shipping label",
    manifest_title: "Exit manifest",
    manifest_copy: "Manifest copy",
    quote_title: "Quotation",
    quote_ref: "Reference",
    receipt_title: "Receipt",
    photo_evidence: "Photo evidence",
    sender: "Sender",
    receiver: "Receiver",
    from: "FROM",
    to: "TO",
    address: "Address",
    phone: "Phone",
    email: "Email",
    tax_id: "Tax ID",
    package: "Package",
    packages: "Packages",
    piece: "Piece",
    pieces: "Pieces",
    description: "Description",
    contents: "Contents",
    items: "Items",
    box: "Box",
    boxes: "Boxes",
    shipments: "Shipments",
    count: "Count",
    route: "Route",
    not_found: "Not found",
    base_price: "Base price",
    variable: "Variable",
    margin: "Margin",
    waybill_fee: "Waybill fee",
    merchandise: "Merchandise",
    surcharge: "Surcharge",
    valid_until: "Valid until",
    days: "days",
    invoice_title: "Invoice",
    invoice_number: "Invoice #",
    due_date: "Due date",
    vat: "VAT",
    balance: "Balance",
    paid: "Paid",
    client: "Client",
    status: "Status",
    office: "Branch",
    bank_details: "Bank details",
    remarks: "Remarks",
    statement_title: "Account statement",
    statement_of_cliente: "Client",
    statement_of_proveedor: "Supplier",
    statement_of_oficina: "Branch",
    statement_of_general: "Overview",
    period: "Period",
    all_dates: "Full history",
    opening_balance: "Opening balance",
    closing_balance: "Closing balance",
    total_charges: "Total charges",
    total_credits: "Total credits",
    movement: "Movement",
    charge: "Charge",
    credit: "Credit",
    no_movements: "No movements in this period",
    mov_factura: "Invoice",
    mov_cobro: "Collection",
    mov_gasto: "Expense",
    mov_factura_compra: "Purchase invoice",
    mov_pago: "Payment",
    mov_cargo: "Charge",
    mov_abono: "Credit",
    inv_status_pendiente: "Pending",
    inv_status_parcial: "Partial",
    inv_status_pagada: "Paid",
    footer_tagline: "Meos Cargo · Enterprise logistics platform",
    brand_tagline: "SOLUTIONS THAT MOVE YOUR WORLD",
    brand_slogan: "Committed to your growth",
    invoice_subtitle: "Tax invoice",
    folio: "Folio",
    issue_date: "Issue date",
    payment_method: "Payment method",
    bank_transfer: "Bank transfer",
    client_data: "Client details",
    name: "Name",
    address: "Address",
    amount: "Amount",
    discount: "Discount",
    observations: "Notes",
    thanks_note: "Thank you for your business. Contact us with any questions.",
    authorized_by: "Authorized by:",
    cfdi_note: "Electronic document. Verify authenticity via the QR code.",
    track_at: "Track at",
    signature: "Signature",
    printed_on: "Printed on",
    page: "Page",
    delivery: "Delivery",
    pickup: "Pickup",
    delivered_to: "Delivered to",
    received_by: "Received by",
    id_document: "ID document",
    notes: "Notes",
    // Vouchers (expense / payment)
    expense_voucher: "EXPENSE VOUCHER",
    payment_voucher: "PAYMENT VOUCHER",
    category: "Category",
    supplier: "Supplier",
    method: "Method",
  },
  ar: {
    tracking: "التتبع",
    tracking_code: "رقم التتبع",
    reference: "المرجع",
    payment_info: "بيانات الدفع",
    bank: "البنك",
    beneficiary: "المستفيد",
    date: "التاريخ",
    origin: "المنشأ",
    destination: "الوجهة",
    weight: "الوزن",
    weight_kg: "الوزن (كغ)",
    cbm: "متر مكعب",
    mode: "الوسيلة",
    container: "الحاوية",
    vehicle: "المركبة",
    driver: "السائق",
    departure: "المغادرة",
    arrival: "الوصول",
    total: "الإجمالي",
    subtotal: "المجموع الفرعي",
    quantity: "الكمية",
    unit_price: "سعر الوحدة",
    currency: "العملة",
    mode_aereo: "جوي",
    mode_terrestre: "بري",
    mode_maritimo: "بحري",
    label_title: "بطاقة الشحن",
    manifest_title: "بيان المغادرة",
    manifest_copy: "نسخة البيان",
    quote_title: "عرض السعر",
    quote_ref: "المرجع",
    receipt_title: "إيصال",
    photo_evidence: "الأدلة الفوتوغرافية",
    sender: "المرسل",
    receiver: "المستلم",
    from: "من",
    to: "إلى",
    address: "العنوان",
    phone: "الهاتف",
    email: "البريد الإلكتروني",
    tax_id: "الرقم الضريبي",
    package: "طرد",
    packages: "الطرود",
    piece: "قطعة",
    pieces: "القطع",
    description: "الوصف",
    contents: "المحتويات",
    items: "العناصر",
    box: "صندوق",
    boxes: "الصناديق",
    shipments: "الشحنات",
    count: "العدد",
    route: "المسار",
    not_found: "غير موجود",
    base_price: "السعر الأساسي",
    variable: "المتغير",
    margin: "الهامش",
    waybill_fee: "رسوم البوليصة",
    merchandise: "البضاعة",
    surcharge: "رسوم إضافية",
    valid_until: "صالح حتى",
    days: "أيام",
    invoice_title: "فاتورة",
    invoice_number: "رقم الفاتورة",
    due_date: "تاريخ الاستحقاق",
    vat: "ضريبة القيمة المضافة",
    balance: "الرصيد",
    paid: "المدفوع",
    client: "العميل",
    status: "الحالة",
    office: "الفرع",
    bank_details: "البيانات المصرفية",
    remarks: "ملاحظات",
    statement_title: "كشف حساب",
    statement_of_cliente: "العميل",
    statement_of_proveedor: "المورّد",
    statement_of_oficina: "الفرع",
    statement_of_general: "ملخص عام",
    period: "الفترة",
    all_dates: "كل السجل",
    opening_balance: "الرصيد الافتتاحي",
    closing_balance: "الرصيد الختامي",
    total_charges: "إجمالي المدين",
    total_credits: "إجمالي الدائن",
    movement: "الحركة",
    charge: "مدين",
    credit: "دائن",
    no_movements: "لا توجد حركات في هذه الفترة",
    mov_factura: "فاتورة",
    mov_cobro: "تحصيل",
    mov_gasto: "مصروف",
    mov_factura_compra: "فاتورة شراء",
    mov_pago: "دفعة",
    mov_cargo: "مدين",
    mov_abono: "دائن",
    inv_status_pendiente: "معلّقة",
    inv_status_parcial: "جزئية",
    inv_status_pagada: "مدفوعة",
    footer_tagline: "ميوس كارغو · منصة اللوجستيات المؤسسية",
    brand_tagline: "حلول تحرك عالمك",
    brand_slogan: "ملتزمون بنموّك",
    invoice_subtitle: "فاتورة ضريبية",
    folio: "رقم",
    issue_date: "تاريخ الإصدار",
    payment_method: "طريقة الدفع",
    bank_transfer: "حوالة بنكية",
    client_data: "بيانات العميل",
    name: "الاسم",
    address: "العنوان",
    amount: "المبلغ",
    discount: "خصم",
    observations: "ملاحظات",
    thanks_note: "شكراً لثقتكم. لأي استفسار يرجى التواصل معنا.",
    authorized_by: "التوقيع المعتمد:",
    cfdi_note: "مستند إلكتروني. تحقق من صحته عبر رمز QR.",
    track_at: "تتبع في",
    signature: "التوقيع",
    printed_on: "طبع في",
    page: "صفحة",
    delivery: "التسليم",
    pickup: "الاستلام",
    delivered_to: "سلم إلى",
    received_by: "استلم بواسطة",
    id_document: "وثيقة الهوية",
    notes: "ملاحظات",
    // Vouchers (expense / payment)
    expense_voucher: "إيصال مصروف",
    payment_voucher: "إيصال تحصيل",
    category: "الفئة",
    supplier: "المورّد",
    method: "طريقة الدفع",
  },
};

export function pdfStr(lang) {
  return PDF_STRINGS[lang] || PDF_STRINGS.en;
}

export const isRTL = (lang) => lang === "ar";
